import { type FC, useMemo } from 'react';
import { DatePicker, type DatePickerProps, Form, type FormItemProps } from 'antd';
import i18n from 'i18next';
import moment, { type Moment } from 'moment-timezone';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';

import { isRequiredField } from '@/utilz';
import { DATE_FORMATS, USER_PREFERENCES } from '@/contants/common';
import { getLocalTimeZone } from '@/utils/date';

import useUserPreference from '../../../redux/hooks/useUserPreference';

const DatePickerWithPrefTz = DatePicker.generatePicker({
  ...momentGenerateConfig,
  getNow: () => moment.tz(getLocalTimeZone()),
});

const DatePickerDefault = DatePicker.generatePicker(momentGenerateConfig);

interface FormDatePickerProps
  extends Pick<FormItemProps, 'rules' | 'name'>,
    Pick<DatePickerProps<Moment>, 'disabled' | 'allowClear' | 'tabIndex'> {
  format?: string;
  placeholder?: string;
  label?: string;
  margin?: string;
  editMode?: boolean;
  onChange?: (date: Moment | null, dateString: string | string[]) => void;
  disabledTargetDate?: number;
  disableFuture?: boolean;
  singleLineMode?: boolean;
  disableValidation?: boolean;
  defaultData?: string;
  dataTestId?: string;
  previousDateDisable?: boolean;
  isRequired?: boolean;
  isPrefTzSupport?: boolean;
  showValidation?: boolean;
}

const FormDatePicker: FC<FormDatePickerProps> = ({
  placeholder = i18n.t('translation:default.date'),
  label,
  margin = 'mb-15',
  name = `testFiled-${Math.random()}`,
  rules = [],
  editMode = true,
  disabled,
  onChange,
  disabledTargetDate,
  disableFuture = false,
  format,
  singleLineMode = false,
  disableValidation = false,
  allowClear = true,
  tabIndex,
  defaultData,
  dataTestId,
  isRequired = false,
  isPrefTzSupport = true,
  showValidation = false,
}) => {
  const isFieldRequired = ((rules.length > 0 && isRequiredField(rules) && !disableValidation) || isRequired) ?? false;

  const userDateFormat = useUserPreference(USER_PREFERENCES.DEFAULT_DATE_FORMAT);

  const activeFormat = format || userDateFormat || DATE_FORMATS['MM/DD/YYYY'];

  const defaultValue = useMemo(() => {
    if (!defaultData) {
      return null;
    }

    const parsed = moment(defaultData, activeFormat, true);
    return parsed.isValid() ? parsed : null;
  }, [defaultData, activeFormat]);

  const MyDatePicker = isPrefTzSupport ? DatePickerWithPrefTz : DatePickerDefault;

  return (
    <Form.Item
      className={`${
        singleLineMode ? 'single-line-mode' : ''
      } form-input-container ${margin} ${!editMode ? 'edit-mode-off' : ''} ${showValidation ? 'show-validation' : ''} `}
      name={name}
      {...(label
        ? {
            label: (
              <span className={`${isFieldRequired ? 'fw-700' : ''}`}>
                {label}
                {isFieldRequired && <span className="required">*</span>}
              </span>
            ),
          }
        : {})}
      rules={
        disableValidation
          ? []
          : [
              ...rules,
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }
                  if (!moment.isMoment(value) || !value.isValid()) {
                    return Promise.reject(new Error(i18n.t('translation:default.invalidDate') || 'Invalid date'));
                  }
                  return Promise.resolve();
                },
              },
            ]
      }
      colon={false}
      data-testid={dataTestId}
    >
      <MyDatePicker
        allowClear={allowClear}
        defaultValue={defaultValue}
        format={{
          format: activeFormat,
          type: 'mask',
        }}
        changeOnBlur
        disabledDate={
          disabledTargetDate
            ? (current) => {
                const tz = isPrefTzSupport ? getLocalTimeZone() : moment.tz.guess();
                const customDate = disabledTargetDate
                  ? moment.tz(disabledTargetDate, tz).format('YYYY-MM-DD')
                  : moment.tz(tz).format('YYYY-MM-DD');
                const currentDate = current.clone().format('YYYY-MM-DD');

                if (disableFuture) {
                  return currentDate > customDate;
                }
                return currentDate < customDate;
              }
            : undefined
        }
        placeholder={editMode ? placeholder : ''}
        className="form-datepicker"
        disabled={disabled}
        {...(onChange ? { onChange } : {})}
        id={name}
        tabIndex={tabIndex}
      />
    </Form.Item>
  );
};

export default FormDatePicker;
